import { defineStore } from 'pinia'
import { restaurantApi } from '@/api/restaurant'
import type { ApiResponse, Restaurant, MenuCategory, MenuItem } from '@/interfaces'
import { resolveCurrentRestaurantMealType } from '@/utils/restaurantMealTime'

interface RestaurantState {
  restaurants: Restaurant[]
  currentRestaurant: Restaurant | null
  menuCategories: MenuCategory[]
  menuItems: MenuCategory[]
  activeMealType: number | null
  activeMenuCategoryId: string | null
  serviceCharge: number
  currentTenantId: string | null
  defaultCurrencyCode: string
  currencyRates: Record<string, number>
}

/** Return first valid URL from an array (skip placeholder "blob" or empty url). */
function firstValidUrlFrom(arr: any[]): string | undefined {
  if (!Array.isArray(arr)) return undefined
  for (const entry of arr) {
    const url = entry?.original_url ?? entry?.url ?? entry?.preview_url
    const fileName = entry?.file_name ?? ''
    if (typeof url === 'string' && url.trim() !== '' && String(fileName).toLowerCase() !== 'blob') {
      return url.trim()
    }
  }
  return undefined
}

function firstValidImageUrl(item: any): string | undefined {
  const fromCollection = firstValidUrlFrom(item?.image_collection || [])
  if (fromCollection) return fromCollection
  const fromMedia = firstValidUrlFrom(item?.media || [])
  if (fromMedia) return fromMedia
  return item?.image_url && typeof item.image_url === 'string' ? item.image_url.trim() : undefined
}

function normalizeFilterMenuResponse(raw: any[]): MenuCategory[] {
  if (!Array.isArray(raw)) return []
  return raw.map(cat => {
    const menuItems: MenuItem[] = []
    const items = cat?.menu_items
    if (Array.isArray(items)) {
      items.forEach((row: any) => {
        const item = row?.item ?? row
        if (!item || (!item.title && !item.label)) return
        const imageUrl = firstValidImageUrl(item)
        const price = item.atr_price ?? item.price ?? 0
        const currencyCode = item.currency_code ?? 'LKR'
        menuItems.push({
          id: item.id,
          item_id: row.item_id ?? item.id,
          title: item.title ?? item.label ?? '',
          description: item.description,
          price: typeof price === 'number' ? price : 0,
          discount: item.atr_discount ?? item.discount,
          currency_code: currencyCode,
          ...(imageUrl ? { image_url: imageUrl } : {}),
          item_attributes: item.item_attributes ?? [],
          item_attributes_filter: item.item_attributes_filter,
        } as MenuItem)
      })
    }
    return {
      ...cat,
      menu_items: menuItems,
    } as MenuCategory
  })
}

export const useRestaurantStore = defineStore('restaurant', {
  state: (): RestaurantState => ({
    restaurants: [],
    currentRestaurant: null,
    menuCategories: [],
    menuItems: [],
    activeMealType: null,
    activeMenuCategoryId: null,
    serviceCharge: 0,
    currentTenantId: null,
    defaultCurrencyCode: 'LKR',
    currencyRates: {},
  }),

  getters: {
    /** Flatten all menu items from categories for lookup by item_id */
    allMenuItemsFlat(state): MenuItem[] {
      return (state.menuCategories || []).flatMap(
        (c: MenuCategory) => (c.menu_items || []) as MenuItem[]
      )
    },
  },

  actions: {
    getMenuItemById(itemId: string | number): MenuItem | undefined {
      const list = this.allMenuItemsFlat
      return list.find(
        (m: MenuItem) =>
          String(m.id) === String(itemId) ||
          String((m as { item_id?: string | number }).item_id) === String(itemId)
      )
    },
    async getRestaurantData(
      tenantId: string,
      params: Record<string, any> = {}
    ): Promise<ApiResponse<Restaurant>> {
      this.currentTenantId = tenantId

      const showResponse = await restaurantApi.getRestaurantData(tenantId, params)
      const responseData = (showResponse as any).data || showResponse

      if (!showResponse.error) {
        const data = responseData
        if (data && typeof data === 'object' && !Array.isArray(data)) {
          const rest = { ...(data as Record<string, unknown>) }
          delete rest.menu_categories
          delete rest.menu_items
          this.currentRestaurant = rest as unknown as Restaurant
          if ((data as Restaurant).service_charge != null) {
            this.serviceCharge = (data as Restaurant).service_charge
          }
          if ((data as Restaurant).default_currency_code) {
            this.defaultCurrencyCode = (data as Restaurant).default_currency_code || 'LKR'
          }
          if ((data as Restaurant).currency_rates) {
            this.currencyRates = (data as Restaurant).currency_rates || {}
          }
        }
      }

      const resolvedMealType =
        params.meal_type ??
        resolveCurrentRestaurantMealType((responseData as Restaurant)?.meal_time) ??
        'all'

      this.activeMealType = typeof resolvedMealType === 'number' ? resolvedMealType : null

      const filterResponse = await restaurantApi.filterMenuItems({
        tenant_id: tenantId,
        meal_type: resolvedMealType,
        category_visibility: params.visibility,
      })
      if (!filterResponse.error) {
        const filterData = (filterResponse as any).data ?? filterResponse
        const categories = Array.isArray(filterData) ? filterData : []
        const normalized = normalizeFilterMenuResponse(categories)
        this.menuCategories = normalized
        this.menuItems = normalized
      }
      return showResponse as ApiResponse<Restaurant>
    },

    async getMenuCategoryArray(tenantId: string, params: Record<string, any> = {}) {
      const response = await restaurantApi.getMenuCategoryArray(tenantId, params)
      if (response.error) {
        return response
      }
      const data = (response as any).data || response
      if (Array.isArray(data)) {
        this.menuCategories = data as MenuCategory[]
      }
      return response
    },

    async filterMenuItems(params: {
      tenant_id: string
      meal_type: number | string
      searchParam?: string
      category_visibility?: Array<string>
    }) {
      const response = await restaurantApi.filterMenuItems(params)
      if (response.error) {
        return response
      }
      const data = (response as any).data || response
      if (Array.isArray(data)) {
        this.menuItems = data as MenuCategory[]
      }
      this.activeMealType = typeof params.meal_type === 'number' ? params.meal_type : null
      return response
    },

    async checkTableAvailability(params: { tenant_id: string; table_no: string }) {
      return await restaurantApi.checkTableAvailability(params)
    },

    setActiveMealType(mealType: number | null): void {
      this.activeMealType = mealType
    },

    setActiveMenuCategory(categoryId: string): void {
      this.activeMenuCategoryId = categoryId
    },

    setCurrentTenantId(tenantId: string): void {
      this.currentTenantId = tenantId
    },
  },
})
